import { Modal as MantineModal } from "@mantine/core";
import { isValidElement } from "react";
import styles from "./modal.module.scss";
import type { CustomModalFactory, IProps } from "./modal.types";
import { Icon } from "../icon";
import { faXmarkLarge } from "@fortawesome/pro-solid-svg-icons";
import { ActionIcon } from "../action-icon";
import { Title } from "../title";
import { componentWrapper } from "../../utilities";
import { ModalRoot } from "./modal-root";
import { ModalContent } from "./modal-content";
import { ModalBody, ModalBodyWithSidebar } from "./modal-body";
import { ModalHeader } from "./modal-header";
import { ModalTitle } from "./modal-title";
import { ModalCloseButton } from "./modal-close-button";
import { ModalFooter } from "./modal-footer";
import { rem } from "@mantine/core";

/**
 * Modal component for displaying content in a dialog overlay.
 * @param {string | React.ReactNode} props.title - The title of the modal, can be a string or a React node.
 * @param {string} props.variant - The visual style variant of the modal.
 * @returns {JSX.Element} The Modal component.
 */
const Modal = componentWrapper<CustomModalFactory, IProps>(
    "Modal",
    ({ title, variant = "default", ...otherProps }, ref) => {
        return (
            <MantineModal
                ref={ref}
                data-variant={variant}
                title={
                    typeof title === "string" ? (
                        <Title order={4}>{title}</Title>
                    ) : isValidElement(title) ? (
                        title
                    ) : undefined
                }
                closeButtonProps={{
                    icon: (
                        <ActionIcon variant="subtle" size="lg" color="gray">
                            <Icon size="sm" icon={faXmarkLarge} />
                        </ActionIcon>
                    ),
                }}
                classNames={{
                    root: styles["root"],
                    overlay: styles["overlay"],
                    content: styles["content"],
                    body: styles["body"],
                    header: styles["header"],
                    title: styles["title"],
                    close: styles["close"],
                    inner: styles["inner"],
                }}
                maw={rem(1360)}
                {...otherProps}
            />
        );
    }
);

Modal.Root = ModalRoot;
Modal.Overlay = MantineModal.Overlay;
Modal.Content = ModalContent;
Modal.Body = ModalBody;
Modal.BodyWithSidebar = ModalBodyWithSidebar;
Modal.Header = ModalHeader;
Modal.Title = ModalTitle;
Modal.CloseButton = ModalCloseButton;
Modal.Stack = MantineModal.Stack;
Modal.Footer = ModalFooter;

export { Modal };
